import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { 
  EmergencyContactSection, 
  defaultEmergencyContactValues,
  buildEmergencyContactPayload,
  deriveEmergencyContactAddress,
} from "./EmergencyContactSection";
import type { AddressLike } from "@/lib/address";
import type { EmergencyContact } from "@shared/schema";
import { normalizeAddressFields } from "@/lib/address";

type EmergencyContactModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    name: string;
    phone: string;
    relationship: string;
    contactType: string;
    isPrimary: boolean;
    email?: string | null;
    address?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    county?: string | null;
    country?: string;
  }) => void;
  editingContact?: EmergencyContact | null;
  employeeName?: string;
  employeeAddress?: AddressLike;
  isPending?: boolean;
};

const emergencyContactSchema = z.object({
  emergencyContact: z.string().min(1, "Full name is required"),
  emergencyPhone: z.string().min(1, "Phone number is required"),
  emergencyRelationship: z.string().min(1, "Relationship is required"),
  emergencyContactType: z.string().default("Primary"),
  emergencySameAddress: z.boolean().default(false),
  email: z.union([z.string().email("Invalid email address"), z.literal(""), z.null()]).optional(),
  emergencyAddress1: z.string().optional(),
  emergencyAddress2: z.string().optional(),
  emergencyCity: z.string().optional(),
  emergencyState: z.string().optional(),
  emergencyZipCode: z.string().optional(),
  emergencyCounty: z.string().optional(),
  emergencyCountry: z.string().default("United States"),
});

export function EmergencyContactModal({
  open,
  onOpenChange,
  onSubmit,
  editingContact,
  employeeName,
  employeeAddress,
  isPending = false,
}: EmergencyContactModalProps) {
  const { toast } = useToast();

  const form = useForm({
    resolver: zodResolver(emergencyContactSchema),
    defaultValues: {
      ...defaultEmergencyContactValues,
      email: "",
    },
  });

  const handleSubmit = (data: any) => {
    // Use the helper function to build the payload, which handles address logic
    const defaultEmployeeAddress: AddressLike = employeeAddress || {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
      country: "United States",
    };

    const payload = buildEmergencyContactPayload(data, defaultEmployeeAddress);
    
    if (!payload) {
      return;
    }

    onSubmit({
      name: payload.name,
      phone: payload.phone,
      relationship: payload.relationship,
      contactType: payload.contactType || "Primary",
      isPrimary: payload.isPrimary ?? (payload.contactType === "Primary"),
      email: data.email || null,
      address: payload.address || null,
      address1: payload.address1 || null,
      address2: payload.address2 || null,
      city: payload.city || null,
      state: payload.state || null,
      zipCode: payload.zipCode || null,
      county: payload.county || null,
      country: payload.country || "United States",
    });

    form.reset();
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
    }
    onOpenChange(newOpen);
  };

  // Populate form when editing
  React.useEffect(() => {
    if (editingContact && open) {
      const addressDefaults = normalizeAddressFields(editingContact);
      form.reset({
        emergencyContact: editingContact.name || "",
        emergencyPhone: editingContact.phone || "",
        emergencyRelationship: editingContact.relationship || "",
        emergencyContactType: editingContact.contactType || "Primary",
        emergencySameAddress: false,
        email: editingContact.email || "",
        emergencyAddress1: addressDefaults.address1 || "",
        emergencyAddress2: addressDefaults.address2 || "",
        emergencyCity: addressDefaults.city || "",
        emergencyState: addressDefaults.state || "",
        emergencyZipCode: addressDefaults.zipCode || "",
        emergencyCounty: addressDefaults.county || "",
        emergencyCountry: addressDefaults.country || "United States",
      } as any);
    } else if (!editingContact && open) {
      form.reset({
        ...defaultEmergencyContactValues,
        emergencySameAddress: false, // Show address fields by default when creating
        email: "",
      });
    }
  }, [editingContact, open, form]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingContact ? "Edit Emergency Contact" : "Add Emergency Contact"}
          </DialogTitle>
          {employeeName && (
            <DialogDescription>
              {editingContact
                ? `Update emergency contact information for ${employeeName}`
                : `Add an emergency contact for ${employeeName}`}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <EmergencyContactSection form={form} />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? editingContact
                  ? "Updating..."
                  : "Adding..."
                : editingContact
                ? "Update Contact"
                : "Add Contact"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

